import React, { useEffect, useRef, useState } from "react";
import type { ReportsService } from "../server/reports.ts";
import { api, Button, Banner, Field, Empty } from "./ui.tsx";
import "./reports.css";
import { Count } from "./visits-presentation.tsx";
type Report = Awaited<ReturnType<ReportsService["summary"]>>;
export function Reports() {
  const [access, setAccess] = useState<{
      users: { id: string; name: string }[];
      canExport: boolean;
    } | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [report, setReport] = useState<Report | null>(null);
  const [draft, setDraft] = useState({
      from: "",
      to: "",
      assessor: "",
      compare: "",
    }),
    [filters, setFilters] = useState(draft),
    [details, setDetails] = useState<{
      area: string;
      assessor: string;
      offset: number;
    } | null>(null),
    [records, setRecords] = useState<any>(null),
    [retry, setRetry] = useState(0);
  const title = useRef<HTMLHeadingElement>(null),
    detailTitle = useRef<HTMLHeadingElement>(null);
  const params = (q: Record<string, any>) =>
    new URLSearchParams(
      Object.entries(q)
        .filter(([, v]) => v !== "" && v !== undefined)
        .map(([k, v]) => [k, String(v)]),
    ).toString();
  useEffect(() => {
    if (report) {
      title.current?.focus();
      if (report.items.length > 1)
        title.current?.scrollIntoView({ block: "start" });
    }
  }, [report]);
  useEffect(() => {
    const c = new AbortController();
    api("/reports/access", "GET", undefined, c.signal)
      .then(setAccess)
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      });
    return () => c.abort();
  }, [retry]);
  useEffect(() => {
    const c = new AbortController();
    setBusy(true);
    setError("");
    setReport(null);
    setDetails(null);
    api<Report>(
      "/reports/summary?" + params(filters),
      "GET",
      undefined,
      c.signal,
    )
      .then((r) => {
        setReport(r);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      })
      .finally(() => {
        if (!c.signal.aborted) setBusy(false);
      });
    return () => c.abort();
  }, [filters, retry]);
  useEffect(() => {
    if (!details) return;
    const c = new AbortController();
    setRecords(null);
    api(
      "/reports/demands?" + params({ ...filters, compare: "", ...details }),
      "GET",
      undefined,
      c.signal,
    )
      .then(setRecords)
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      });
    return () => c.abort();
  }, [details, filters]);
  const show = (area: string, assessor: string) => {
    setRecords(null);
    setDetails({ area, assessor, offset: 0 });
  };
  useEffect(() => {
    if (details && records) {
      detailTitle.current?.focus();
      detailTitle.current?.scrollIntoView({ block: "start" });
    }
  }, [details, records]);
  const number = (v: number | null | undefined) =>
    v === null || v === undefined
      ? "Indisponível"
      : v.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  async function download() {
    setBusy(true);
    setError("");
    try {
      const blob = await api<Blob>(
        "/reports/pdf?" + params(filters),
        "GET",
        undefined,
        undefined,
        "pdf",
      );
      const url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = "relatorio-gabinete.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="reports-module">
      <header className="reports-heading">
        <div>
          <h1>Relatórios</h1>
          <p className="muted">
            Demandas, visitas e resultados no seu escopo autorizado.
          </p>
        </div>
        {access?.canExport && (
          <Button onClick={download} busy={busy}>
            Exportar PDF
          </Button>
        )}
      </header>
      {error && (
        <Banner kind="danger">
          {error}{" "}
          <Button onClick={() => setRetry((n) => n + 1)}>
            Tentar novamente
          </Button>
        </Banner>
      )}
      <form
        className="reports-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters({ ...draft });
        }}
      >
        <Field label="De">
          <input
            type="date"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
        </Field>
        <Field label="Até">
          <input
            type="date"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
        </Field>
        <Field label="Assessor">
          <select
            value={draft.assessor}
            onChange={(e) =>
              setDraft({
                ...draft,
                assessor: e.target.value,
                compare: e.target.value ? draft.compare : "",
              })
            }
          >
            <option value="">Meu escopo autorizado</option>
            {access?.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Comparar com">
          <select
            value={draft.compare}
            disabled={!draft.assessor}
            onChange={(e) => setDraft({ ...draft, compare: e.target.value })}
          >
            <option value="">Sem comparação</option>
            {access?.users
              .filter((u) => u.id !== draft.assessor)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </select>
        </Field>
        <div className="reports-actions">
          <Button type="submit" kind="primary" busy={busy}>
            Aplicar
          </Button>
          <Button
            onClick={() => {
              const clear = { from: "", to: "", assessor: "", compare: "" };
              setDraft(clear);
              setFilters(clear);
            }}
          >
            Limpar
          </Button>
        </div>
      </form>
      {busy && !report && (
        <p role="status">Consultando os dados autorizados…</p>
      )}
      {report && (
        <>
          <h2 ref={title} tabIndex={-1}>
            {report.items.length > 1
              ? "Comparação por assessor"
              : "Resumo do relatório"}
          </h2>
          <p className="small muted">
            {filters.from || "Início disponível"} até{" "}
            {filters.to || "data atual"} · Gerado em{" "}
            {new Date(report.at).toLocaleString("pt-BR")}
          </p>
          {report.items.length === 2 && (
            <section
              className="reports-surface"
              aria-label="Indicadores da comparação"
            >
              {[
                [
                  "Demandas cadastradas",
                  (i: Report["items"][number]) => number(i.demands?.total),
                ],
                [
                  "Demandas em aberto",
                  (i: Report["items"][number]) => number(i.demands?.active),
                ],
                [
                  "Pontos concedidos",
                  (i: Report["items"][number]) =>
                    i.results?.points ?? "Sem acesso",
                ],
                [
                  "XP concedido",
                  (i: Report["items"][number]) => i.results?.xp ?? "Sem acesso",
                ],
                [
                  "Primeiras visitas no período",
                  (i: Report["items"][number]) =>
                    number(i.results?.firstVisits),
                ],
                [
                  "Endereços únicos atuais",
                  (i: Report["items"][number]) =>
                    number(i.visits?.metrics.uniqueAddresses),
                ],
                [
                  "Pendentes atuais",
                  (i: Report["items"][number]) =>
                    number(i.visits?.metrics.pending),
                ],
                [
                  "Em análise atualmente",
                  (i: Report["items"][number]) =>
                    number(i.visits?.metrics.analysis),
                ],
                [
                  "Cobertura atual",
                  (i: Report["items"][number]) =>
                    i.visits?.metrics.coverage == null
                      ? "Indisponível"
                      : number(i.visits.metrics.coverage) + "%",
                ],
              ].map(([label, get]) => (
                <div className="reports-comparison-row" key={String(label)}>
                  <h3>{String(label)}</h3>
                  {report.items.map((i) => (
                    <p key={i.id}>
                      <span>{i.name}</span>
                      <strong>
                        {(get as (i: Report["items"][number]) => string)(i)}
                      </strong>
                    </p>
                  ))}
                </div>
              ))}
            </section>
          )}
          <div
            className={
              "reports-columns" +
              (report.items.length > 1 ? " reports-compare" : "")
            }
          >
            {report.items.map((item) => (
              <article className="reports-column" key={item.id ?? "scope"}>
                <h3>{item.name}</h3>
                <details open={report.items.length === 1}>
                  <summary>Detalhar indicadores de {item.name}</summary>
                  <section className="reports-surface">
                    <h4>Demandas cadastradas no período</h4>
                    <p className="small muted">
                      Situação atual dessas demandas, sem a lixeira.
                    </p>
                    {item.demands ? (
                      <>
                        <strong className="reports-total">
                          <Count
                            value={item.demands.total}
                            scope={"reports-" + (item.id ?? "scope")}
                            label="demandas"
                          />{" "}
                          <span>demandas</span>
                        </strong>
                        <div className="reports-metrics">
                          {(
                            [
                              ["active", "Em aberto", item.demands.active],
                              [
                                "awaiting_acceptance",
                                "Aguardando aceite",
                                item.demands.awaiting,
                              ],
                              ["closed", "Encerradas", item.demands.closed],
                            ] as const
                          ).map(([area, label, value]) => (
                            <button
                              key={area}
                              onClick={() => show(area, item.id ?? "")}
                            >
                              <strong>{number(value)}</strong>
                              <span>{label}</span>
                              <small>Ver demandas ›</small>
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Empty title="Nenhum dado disponível">
                        Sem acesso a demandas deste recorte.
                      </Empty>
                    )}
                  </section>
                  <section className="reports-surface">
                    <h4>Resultados confirmados no período</h4>
                    {item.results ? (
                      <>
                        <div className="reports-metrics">
                          <div>
                            <strong>{item.results.points}</strong>
                            <span>Pontos concedidos</span>
                          </div>
                          <div>
                            <strong>{item.results.xp}</strong>
                            <span>XP concedido</span>
                          </div>
                          <div>
                            <strong>{number(item.results.demands)}</strong>
                            <span>Conclusões de demandas com participação</span>
                          </div>
                          <div>
                            <strong>{number(item.results.firstVisits)}</strong>
                            <span>Primeiras visitas registradas</span>
                          </div>
                        </div>
                        <p className="small muted">
                          Somente recompensas registradas e eventos canônicos.
                          Conclusões com vários participantes não devem ser
                          somadas como demandas únicas.
                        </p>
                        <a href="?view=rankings">
                          Consultar rankings e critérios ›
                        </a>
                      </>
                    ) : (
                      <Empty title="Nenhum dado disponível">
                        Resultados deste assessor fora do seu acesso.
                      </Empty>
                    )}
                  </section>
                  <section className="reports-surface">
                    <h4>Visitas — situação atual da base</h4>
                    <p className="small muted">
                      O período acima não altera esta fotografia atual.
                      Responsável não é o nome da planilha.
                    </p>
                    {item.visits ? (
                      <>
                        <div className="reports-metrics">
                          {(
                            [
                              [
                                "Endereços únicos",
                                item.visits.metrics.uniqueAddresses,
                              ],
                              ["Visitados", item.visits.metrics.visited],
                              ["Pendentes", item.visits.metrics.pending],
                              ["Em análise", item.visits.metrics.analysis],
                            ] as const
                          ).map(([label, value]) => (
                            <div key={label}>
                              <strong>{number(value)}</strong>
                              <span>{label}</span>
                            </div>
                          ))}
                        </div>
                        <p>
                          <b>
                            Cobertura: {number(item.visits.metrics.coverage)}
                            {item.visits.metrics.coverage !== null ? "%" : ""}
                          </b>
                        </p>
                        {item.visits.freshness.state !== "ready" && (
                          <Banner>
                            Base{" "}
                            {item.visits.freshness.state === "partial"
                              ? "parcial"
                              : item.visits.freshness.state === "unavailable"
                                ? "indisponível"
                                : item.visits.freshness.state === "updating"
                                  ? "em atualização"
                                  : "desatualizada"}
                            . Confira o alcance e as fontes antes de interpretar
                            os totais.
                          </Banner>
                        )}
                        {item.visits.metrics.provisional && (
                          <p className="small">
                            Indicador provisório conforme a regra de
                            deduplicação publicada.
                          </p>
                        )}
                        <p className="small muted">
                          Referência:{" "}
                          {new Date(item.visits.freshness.asOf).toLocaleString(
                            "pt-BR",
                          )}
                        </p>
                        <a
                          href={
                            "?view=visits-overview" +
                            (item.id
                              ? "&responsibleId=" + encodeURIComponent(item.id)
                              : "")
                          }
                        >
                          Consultar composição e fontes ›
                        </a>
                      </>
                    ) : (
                      <p>{item.visitsReason}</p>
                    )}
                  </section>
                </details>
              </article>
            ))}
          </div>
          {report.items.length > 1 && (
            <p className="small muted">
              Os dois recortes usam os mesmos indicadores. Os números não
              avaliam automaticamente produtividade, qualidade ou mérito.
            </p>
          )}
          <details className="reports-surface">
            <summary>Como é calculado</summary>
            <p>
              Demandas: registros visíveis ao usuário, criados nas datas
              selecionadas, classificados pela situação atual. Cada demanda
              entra uma vez; a lixeira fica de fora.
            </p>
            <p>
              Resultados: reutilizam os recibos efetivamente registrados, a
              precisão publicada e os eventos canônicos de conclusão e primeira
              visita. As datas usam America/Sao_Paulo.
            </p>
            <p>
              Visitas: reutilizam o consolidado existente, com deduplicação,
              classificações e permissões próprias. O filtro de assessor
              considera somente responsabilidade confirmada. Não representa
              visitas realizadas entre duas datas.
            </p>
            <p>
              A exportação geral em PDF está disponível ao administrador. A
              autorização de leitura não concede exportação ou edição.
            </p>
          </details>
          {details && (
            <section
              className="reports-surface"
              aria-label="Composição das demandas"
            >
              <div className="reports-heading">
                <h2 ref={detailTitle} tabIndex={-1}>
                  Demandas deste resultado
                </h2>
                <Button onClick={() => setDetails(null)}>Fechar lista</Button>
              </div>
              {!records ? (
                <p role="status">Consultando demandas…</p>
              ) : (
                <>
                  <p>
                    {records.total} resultados com os mesmos filtros do
                    indicador.
                  </p>
                  {records.rows.length ? (
                    <ul className="reports-records">
                      {records.rows.map((r: any) => (
                        <li key={r.id}>
                          <strong>{r.citizen.name}</strong>
                          <span>
                            {r.status} · {r.priority}
                          </span>
                          <span>
                            Cadastrada em{" "}
                            {new Date(r.createdAt).toLocaleDateString("pt-BR")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Empty title="Nenhum dado disponível">
                      Nenhuma demanda neste resultado.
                    </Empty>
                  )}
                  <div className="reports-actions">
                    <Button
                      disabled={details.offset === 0}
                      onClick={() =>
                        setDetails({
                          ...details,
                          offset: Math.max(0, details.offset - 20),
                        })
                      }
                    >
                      Anterior
                    </Button>
                    <Button
                      disabled={details.offset + 20 >= records.total}
                      onClick={() =>
                        setDetails({ ...details, offset: details.offset + 20 })
                      }
                    >
                      Próxima
                    </Button>
                    <a href="?view=demands">Abrir módulo de Demandas ›</a>
                  </div>
                </>
              )}
            </section>
          )}
        </>
      )}
    </section>
  );
}
